<script setup lang="ts">
interface ProjectFeatureMediaProps {
  mode?: 'modal' | 'inline';
  videoUrl: string;
  placeholderText?: string;
  poster?: string;
  class?: string | string[];
}

const props = withDefaults(defineProps<ProjectFeatureMediaProps>(), {
  mode: 'modal',
  placeholderText: '',
  poster: '',
  class: '',
});

const videoPlayer = ref<HTMLVideoElement | null>(null);
const videoPlaying = ref(false);
const videoLoading = ref(false);
const videoReady = ref(false);

function onPlayVideo() {
  if (videoPlaying.value || videoLoading.value) return;

  videoLoading.value = true;
  videoPlayer.value?.play();
}

function onVideoPlaying() {
  videoLoading.value = false;
  videoPlaying.value = true;
}

function onVideoLoaded() {
  videoReady.value = true;
}

function onVideoEnded() {
  videoPlaying.value = false;
  videoLoading.value = false;
  videoReady.value = false;
}
</script>

<template>
  <div v-if="mode === 'modal'" class="project-feature-media project-feature-media--modal" :class="props.class">
    <img v-if="poster" class="project-feature-media__background" :src="poster" alt="" />
    <div class="project-feature-media__overlay" aria-hidden="true" />
    <VideoPlayer :video-url="videoUrl" :placeholder-text="placeholderText">
      <slot>
        <img v-if="poster" class="project-feature-media__poster" :src="poster" alt="" />
      </slot>
    </VideoPlayer>
  </div>

  <div v-else class="project-feature-media project-feature-media--inline" :class="props.class">
    <img v-if="poster" class="project-feature-media__background" :src="poster" alt="" />
    <div v-if="!videoPlaying || !videoReady" class="project-feature-media__overlay" @click="onPlayVideo" />
    <div v-if="!videoPlaying || !videoReady" class="project-feature-media__preview" @click="onPlayVideo">
      <img v-if="poster" :src="poster" alt="" />
      <span class="project-feature-media__play" aria-hidden="true" />
      <span class="project-feature-media__label">{{ placeholderText || 'Watch video' }}</span>
    </div>
    <video ref="videoPlayer" :src="videoUrl" :poster="poster" preload="metadata" playsinline controls :class="{'project-feature-media__prevent-click': !videoPlaying}" class="aspect-video" @loadeddata="onVideoLoaded" @playing="onVideoPlaying" @ended="onVideoEnded" />
  </div>
</template>

<style lang="scss">
.project-feature-media {
  height: 100%;
  max-width: 100%;
  min-height: 0;
  position: relative;

  &__background {
    display: block;
    height: 100%;
    inset: 0;
    object-fit: cover;
    pointer-events: none;
    position: absolute;
    width: 100%;
    z-index: 0;
  }

  &__poster {
    display: block;
    height: 100%;
    min-height: 0;
    object-fit: cover;
    pointer-events: none;
    width: 100%;
  }

  &__overlay {
    align-items: center;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    height: 100%;
    inset: 0;
    justify-content: center;
    position: absolute;
    width: 100%;
    z-index: 1;
  }

  &--modal {
    align-items: center;
    display: flex;
    justify-content: center;

    .video-player-container {
      align-items: center;
      aspect-ratio: 16 / 9;
      box-shadow:
        0 0 24px rgba(0, 0, 0, 0.8),
        0 10px 30px rgba(0, 0, 0, 0.28);
      display: flex;
      height: auto;
      justify-content: center;
      position: relative;
      width: 90%;
      z-index: 2;
    }

    .video-placeholder {
      aspect-ratio: 16 / 9;
      height: auto;
      max-width: none;
      overflow: hidden;
      width: 100%;
    }

    .project-feature-media__overlay {
      pointer-events: none;
    }
  }

  &.project-feature-media--inline {
    aspect-ratio: 16 / 9;
    height: auto;
    width: 100%;

    .project-feature-media__overlay {
      cursor: pointer;
      z-index: 2;
    }

    video {
      aspect-ratio: auto;
      display: block;
      height: 100%;
      inset: 0;
      max-height: none;
      max-width: none;
      position: absolute;
      width: 100%;
    }
  }

  &__preview {
    align-items: center;
    aspect-ratio: 16 / 9;
    cursor: pointer;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    height: auto;
    left: 50%;
    overflow: hidden;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    z-index: 3;

    img {
      display: block;
      height: 100%;
      inset: 0;
      object-fit: cover;
      pointer-events: none;
      position: absolute;
      width: 100%;
      z-index: 0;
    }
  }

  &__play {
    border-color: transparent transparent transparent #fff;
    border-style: solid;
    border-width: 24px 0 24px 38px;
    height: 0;
    left: 50%;
    position: absolute;
    top: 46%;
    transform: translate(-25%, -50%);
    width: 0;
    z-index: 1;
  }

  &__label {
    color: #fff;
    font-weight: 700;
    position: absolute;
    top: 62%;
    transform: translateY(-50%);
    white-space: nowrap;
    z-index: 1;
  }

  video {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &__prevent-click {
    pointer-events: none;
  }
}
</style>
